import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Info } from '@lifeomic/chromicons';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import { type MemberContactField } from './memberContactEdit';
import {
  type ContactFieldSyncStatus,
  getContactSyncIssue,
} from './memberContactSync';

const messages = defineMessages({
  emailLabel: {
    id: 'MemberContactSync.emailLabel',
    defaultMessage: 'email',
  },
  phoneLabel: {
    id: 'MemberContactSync.phoneLabel',
    defaultMessage: 'phone',
  },
  contentReason: {
    id: 'MemberContactSync.contentReason',
    defaultMessage:
      'App login {field} ({appLogin}) is out of sync with the snapshot.',
  },
  contentReasonNoValue: {
    id: 'MemberContactSync.contentReasonNoValue',
    defaultMessage: 'App login {field} is out of sync with the snapshot.',
  },
  unverifiedReason: {
    id: 'MemberContactSync.unverifiedReason',
    defaultMessage: 'App login {field} is not verified.',
  },
  syncButton: {
    id: 'MemberContactSync.syncButton',
    defaultMessage: 'Sync {field}',
  },
});

export type MemberContactSyncIndicatorProps = {
  field: MemberContactField;
  status: ContactFieldSyncStatus;
  onSync: () => void;
  isSyncing: boolean;
};

/**
 * Surfaces when a member's app-login email/phone is out of sync with the
 * onboarding snapshot (content mismatch or unverified) and offers an explicit
 * Sync action. Renders nothing when the field is in sync.
 */
export const MemberContactSyncIndicator: React.FC<
  MemberContactSyncIndicatorProps
> = ({ field, status, onSync, isSyncing }) => {
  const { formatMessage } = useIntl();
  const issue = getContactSyncIssue(status);
  if (!issue) {
    return null;
  }

  const fieldLabel = formatMessage(
    field === 'phone' ? messages.phoneLabel : messages.emailLabel,
  );

  const reason =
    issue === 'unverified'
      ? formatMessage(messages.unverifiedReason, { field: fieldLabel })
      : status.appLogin
      ? formatMessage(messages.contentReason, {
          field: fieldLabel,
          appLogin: status.appLogin,
        })
      : formatMessage(messages.contentReasonNoValue, { field: fieldLabel });

  return (
    <Alert
      statusType="warning"
      fullWidth
      {...getTestProps(`contact-sync-${field}`)}
    >
      <AlertIcon icon={Info} />
      <AlertBody>
        <Box direction="row" align="center" gap={1} flexWrap>
          <Text size="subbody">{reason}</Text>
          <Button
            variant="text"
            disabled={isSyncing}
            onClick={onSync}
            {...getTestProps(`contact-sync-action-${field}`)}
          >
            {formatMessage(messages.syncButton, { field: fieldLabel })}
          </Button>
        </Box>
      </AlertBody>
    </Alert>
  );
};
